import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Input } from 'antd';
import { AppDispatch, RootState } from '../../redux/store';
import { createDeviceType, updateDeviceType, fetchDeviceTypes } from '../../redux/DeviceTypes';
import { DeviceTypeFormData, DeviceTypeFormProps } from './types';
import { StyledModal, ModalFooter, SaveButton, CancelButton } from './styles';
import { LABELS, MESSAGES, PLACEHOLDERS, VALIDATION_RULES } from './constants';
import { ThemeProvider } from 'styled-components';
import { notification } from '../../commonComponents/Notification/Notification';

export const DeviceTypeForm: React.FC<DeviceTypeFormProps> = ({
  isOpen,
  initialDeviceType,
  onSave,
  onCancel,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { theme } = useSelector((state: RootState) => state.theme);
  const { loading } = useSelector((state: RootState) => state.deviceTypes);
  const themeMode = theme === 'dark' ? 'dark' : 'light';

  const [form] = Form.useForm<DeviceTypeFormData>();
  const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);
  const [unsavedModalVisible, setUnsavedModalVisible] = useState(false);
  const isEditMode = !!initialDeviceType;

  useEffect(() => {
    if (isOpen) {
      if (initialDeviceType) {
        form.setFieldsValue({
          name: initialDeviceType.name,
        });
      } else {
        form.resetFields();
      }
      setIsUnsavedChanges(false);
    }
  }, [initialDeviceType, form, isOpen]);

  const handleFinish = async (values: DeviceTypeFormData) => {
    try {
      if (isEditMode && initialDeviceType) {
        await dispatch(updateDeviceType({
            id: initialDeviceType.id,
            name: values.name,
          })
        ).unwrap();
        notification.success(MESSAGES.SUCCESS.UPDATED);
      } else {
        await dispatch(createDeviceType({ name: values.name })).unwrap();
        notification.success(MESSAGES.SUCCESS.CREATED);
      }
      dispatch(fetchDeviceTypes());
      setIsUnsavedChanges(false);
      onSave();
    } catch (error: any) {
      notification.error(error || (isEditMode ? MESSAGES.ERROR.UPDATE : MESSAGES.ERROR.CREATE));
    }
  };

  const handleCancel = () => {
    if (isUnsavedChanges) {
      setUnsavedModalVisible(true);
    } else {
      onCancel();
    }
  };

  const handleConfirmCancel = () => {
    setIsUnsavedChanges(false);
    setUnsavedModalVisible(false);
    onCancel();
  };

  const handleValuesChange = () => {
    setIsUnsavedChanges(true);
  };

  return (
    <ThemeProvider theme={{ mode: themeMode }}>
      <StyledModal
        title={isEditMode ? `Edit ${LABELS.DEVICE_TYPE_LIST}` : `Add ${LABELS.DEVICE_TYPE_LIST}`}
        open={isOpen}
        onCancel={handleCancel}
        width={600}
        destroyOnClose
        maskClosable={false}
        footer={
          <ModalFooter>
            <CancelButton onClick={handleCancel} disabled={loading}>
              {LABELS.CANCEL}
            </CancelButton>
            <SaveButton
              type="primary"
              onClick={() => form.submit()}
              loading={loading}
              disabled={loading}
            >
              {LABELS.SAVE}
            </SaveButton>
          </ModalFooter>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          onValuesChange={handleValuesChange}
        >
          <Form.Item
            name="name"
            label={LABELS.NAME}
            rules={VALIDATION_RULES.NAME}
          >
            <Input
              placeholder={PLACEHOLDERS.NAME}
              size="large"
            />
          </Form.Item>
        </Form>
      </StyledModal>

      <StyledModal
        title={MESSAGES.CONFIRM.UNSAVED_TITLE}
        open={unsavedModalVisible}
        onOk={handleConfirmCancel}
        onCancel={() => setUnsavedModalVisible(false)}
        okText="Yes"
        cancelText="No"
        okButtonProps={{ danger: true }}
      >
        <p>{MESSAGES.CONFIRM.UNSAVED_MESSAGE}</p>
      </StyledModal>
    </ThemeProvider>
  );
};
